import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Grid,
  Chip,
  alpha
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// Import authenticated API service instead of raw axios
import { default as api, inspectionCycleRulesApi } from '../../../services/api';

const AddInspectionModal = ({ open, onClose, asset, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [enhancedAsset, setEnhancedAsset] = useState(null);
  const [selectedInspectionType, setSelectedInspectionType] = useState(null);
  const [dateValidation, setDateValidation] = useState({ isValid: true, message: '', suggestedRange: '' });
  const [cycleWarning, setCycleWarning] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    inspection_type_id: '',
    inspection_date: new Date(),
    notes: '',
    cycle: ''
  });

  // Load inspection types when the modal opens
  useEffect(() => {
    if (!open || !asset) return;
    
    // Initialize enhanced asset with the original asset
    setEnhancedAsset(asset);
    
    const fetchResources = async () => {
      setLoadingResources(true);
      try {
        // Check if we have the required IDs
        if (!asset.organization_id || !asset.asset_type_id) {
          console.warn('Asset is missing required data:', { 
            organization_id: asset.organization_id,
            asset_type_id: asset.asset_type_id,
            asset: asset 
          });
          setError('Asset is missing organization ID or asset type ID data');
          setLoadingResources(false);
          return;
        }
        
        // Fetch asset details if organization/network name is missing.
        if (!asset.organization_name || !asset.network_name || !asset.category_id) {
          try {
            const assetResponse = await api.get(`/assets/${asset.id}`);
            if (assetResponse.data) {
              const updatedAsset = {
                ...asset,
                organization_name: assetResponse.data.organization_name,
                network_name: assetResponse.data.network_name,
                category_id: asset.category_id || assetResponse.data.category_id,
                category_name: asset.category_name || assetResponse.data.category_name
              };
              setEnhancedAsset(updatedAsset);
            }
          } catch (error) {
            console.warn('Failed to fetch additional asset details:', error);
            // Continue anyway, as this is not critical
          }
        }
        
        // Fetch inspection types filtered by asset's organization_id and asset_type_id
        const typesResponse = await api.get('/inspections/types', {
          params: { 
            organization_id: asset.organization_id,
            asset_type_id: asset.asset_type_id
          }
        });
        
        if (Array.isArray(typesResponse.data)) {
          setInspectionTypes(typesResponse.data);
          
          // If no inspection types found, show a message
          if (typesResponse.data.length === 0) {
            setError('No inspection types available for this asset');
          }
        }
        
      } catch (err) {
        console.error('Error fetching resources:', err);
        let errorMessage = 'Failed to load inspection types';
        
        if (err.response) {
          errorMessage += `: ${err.response.status} - ${err.response.data?.error || ''}`;
        }
        
        setError(errorMessage);
      } finally {
        setLoadingResources(false);
      }
    };
    
    fetchResources();
    
    // Reset form data when modal opens
    setFormData({
      inspection_type_id: '',
      inspection_date: new Date(),
      notes: '',
      cycle: ''
    });
    setSuccess(false);
    setError(null);
    setSelectedInspectionType(null);
    setDateValidation({ isValid: true, message: '', suggestedRange: '' });
    setCycleWarning(null);
  }, [open, asset]);

  // Validate date when inspection type or cycle changes
  useEffect(() => {
    if (selectedInspectionType?.cycle_frequency && formData.cycle && formData.inspection_date) {
      const validation = validateInspectionDate(
        formData.inspection_date,
        selectedInspectionType.cycle_frequency,
        formData.cycle
      );
      setDateValidation(validation);
    } else {
      setDateValidation({ isValid: true, message: '', suggestedRange: '' });
    }
  }, [selectedInspectionType, formData.cycle, formData.inspection_date]);

  // Check for duplicate cycle when cycle, inspection type, or date changes
  useEffect(() => {
    if (!open || !asset || !formData.cycle || !formData.inspection_type_id || !formData.inspection_date) {
      setCycleWarning(null);
      return;
    }

    const checkCycle = async () => {
      try {
        const year = new Date(formData.inspection_date).getFullYear();
        const response = await api.get('/inspections/check-cycle', {
          params: {
            asset_id: asset.id,
            inspection_type_id: formData.inspection_type_id,
            cycle: formData.cycle,
            year
          }
        });
        if (response.data.exists) {
          setCycleWarning(`An inspection with this cycle already exists (ID: ${response.data.inspection_id})`);
        } else {
          setCycleWarning(null);
        }
      } catch (err) {
        console.error('Failed to check cycle:', err);
      }
    };

    checkCycle();
  }, [open, asset, formData.cycle, formData.inspection_type_id, formData.inspection_date]);

  // Fetch effective cycle when inspection type changes
  useEffect(() => {
    if (selectedInspectionType && asset) {
      const fetchEffectiveCycle = async () => {
        try {
          const response = await inspectionCycleRulesApi.getEffectiveCycle({
            asset_id: asset.id,
            inspection_type_id: selectedInspectionType.id,
          });
          setSelectedInspectionType(prev => ({
            ...prev,
            cycle_frequency: response.data.cycle_frequency,
          }));
        } catch (err) {
          console.error('Failed to fetch effective inspection cycle:', err);
        }
      };
      fetchEffectiveCycle();
    }
  }, [selectedInspectionType?.id, asset]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If inspection type is selected, find and set the selected inspection type details
    if (name === 'inspection_type_id' && value) {
      const selectedType = inspectionTypes.find(type => type.id === value);
      setSelectedInspectionType(selectedType);
      // Reset cycle when inspection type changes as the available cycles will be different
      setFormData(prev => ({
        ...prev,
        [name]: value,
        cycle: '' // Clear cycle selection
      }));
      // Reset date validation
      setDateValidation({ isValid: true, message: '', suggestedRange: '' });
      return; // Early return to avoid updating formData twice
    } else if (name === 'inspection_type_id' && !value) {
      // Clear selected inspection type if no value selected
      setSelectedInspectionType(null);
      // Also clear cycle
      setFormData(prev => ({
        ...prev,
        [name]: value,
        cycle: ''
      }));
      // Reset date validation
      setDateValidation({ isValid: true, message: '', suggestedRange: '' });
      return; // Early return to avoid updating formData twice
    }
    
    // Update form data
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    
    // If cycle is changed, validate the date
    if (name === 'cycle' && selectedInspectionType?.cycle_frequency) {
      const validation = validateInspectionDate(
        formData.inspection_date, 
        selectedInspectionType.cycle_frequency, 
        value
      );
      setDateValidation(validation);
    }
  };
  
  // Handle date change
  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      inspection_date: newDate
    });
    
    // Validate the new date if we have inspection type and cycle
    if (selectedInspectionType?.cycle_frequency && formData.cycle) {
      const validation = validateInspectionDate(
        newDate, 
        selectedInspectionType.cycle_frequency, 
        formData.cycle
      );
      setDateValidation(validation);
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!asset) {
      setError('No asset selected');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create the inspection data
      const inspectionData = {
        ...formData,
        asset_id: asset.id,
        status: 'draft'
      };
      
      // Send the request to create a new inspection
      const response = await api.post('/inspections', inspectionData);
      
      // Set success status
      setSuccess(true);
      
      // Call the success callback with the new inspection
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Close the modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 800);
      
    } catch (err) {
      let errorMessage = 'Failed to create inspection';
      
      if (err.response) {
        errorMessage = `API Error: ${err.response.status} - ${err.response.data?.error || ''}`;
      } else if (err.request) {
        errorMessage = 'No response received from server';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Get cycle range text for display
  const getCycleRangeText = (frequency) => {
    if (!frequency) return '';
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        return 'Week 1-52 (weeks of the year)';
      case 'monthly':
        return 'Month 1-12 (months of the year)';
      case 'quarterly':
        return 'Quarter 1-4 (quarters of the year)';
      case 'biannual':
        return 'Half-Year 1-2 (halves of the year)';
      case 'annual':
        return 'Year 1 (once per year)';
      case 'ad-hoc':
        return 'Ad-hoc inspection (as needed)';
      case 'bimonthly':
        return 'Period 1-6 (every 2 months)';
      case 'every_4_months':
        return 'Period 1-3 (every 4 months)';
      case 'every_18_months':
        return 'Cycle 1 (every 18 months)';
      case 'biennial':
        return 'Cycle 1 (every 2 years)';
      default:
        return 'Cycle not applicable';
    }
  };

  // Calculate expected date range for a given frequency and cycle
  const getExpectedDateRange = (frequency, cycle, baseYear = null) => {
    if (!frequency || !cycle) return null;
    
    const year = baseYear || new Date().getFullYear();
    const cycleNum = parseInt(cycle);
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        // Calculate the start and end of the specified week
        // Using Monday as the start of the week (ISO standard)
        const jan1 = new Date(year, 0, 1);
        const firstMonday = new Date(jan1);
        const dayOfWeek = jan1.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const daysToFirstMonday = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7;
        firstMonday.setDate(jan1.getDate() + daysToFirstMonday);
        
        const weekStart = new Date(firstMonday);
        weekStart.setDate(firstMonday.getDate() + (cycleNum - 1) * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return { start: weekStart, end: weekEnd };
        
      case 'monthly':
        // The specified month
        const monthStart = new Date(year, cycleNum - 1, 1);
        const monthEnd = new Date(year, cycleNum, 0); // Last day of the month
        return { start: monthStart, end: monthEnd };
        
      case 'quarterly':
        // Calculate quarter dates
        const quarterStart = new Date(year, (cycleNum - 1) * 3, 1);
        const quarterEnd = new Date(year, cycleNum * 3, 0); // Last day of the quarter
        return { start: quarterStart, end: quarterEnd };
        
      case 'biannual':
        // First half (Jan-Jun) or second half (Jul-Dec)
        const halfStart = new Date(year, cycleNum === 1 ? 0 : 6, 1);
        const halfEnd = new Date(year, cycleNum === 1 ? 6 : 12, 0);
        return { start: halfStart, end: halfEnd };
        
      case 'annual':
        // Any time in the year
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);
        return { start: yearStart, end: yearEnd };
        
      case 'ad-hoc':
        // No specific range for ad-hoc
        return null;
        
      default:
        return null;
    }
  };

  // Validate inspection date against expected range
  const validateInspectionDate = (date, frequency, cycle) => {
    if (!date || !frequency || !cycle) {
      return { isValid: true, message: '', suggestedRange: '' };
    }

    if (frequency.toLowerCase() === 'ad-hoc') {
      return { isValid: true, message: 'Ad-hoc inspections can be scheduled at any time.', suggestedRange: '' };
    }

    const selectedDate = new Date(date);
    const cycleNum = parseInt(cycle);
    const expectedRange = getExpectedDateRange(frequency, cycle, selectedDate.getFullYear());
    
    if (!expectedRange) {
      return { isValid: true, message: '', suggestedRange: '' };
    }

    const isWithinRange = selectedDate >= expectedRange.start && selectedDate <= expectedRange.end;
    
    const formatDate = (date) => date.toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });

    if (isWithinRange) {
      let periodName = '';
      switch (frequency.toLowerCase()) {
        case 'weekly':
          periodName = `Week ${cycle}`;
          break;
        case 'monthly':
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          periodName = monthNames[cycleNum - 1];
          break;
        case 'quarterly':
          periodName = `Q${cycle}`;
          break;
        case 'biannual':
          periodName = cycle === 1 ? '1st Half' : '2nd Half';
          break;
        case 'annual':
          periodName = selectedDate.getFullYear().toString();
          break;
        default:
          periodName = `${frequency} ${cycle}`;
      }
      
      return { 
        isValid: true, 
        message: `✓ Date is within ${periodName} (${formatDate(expectedRange.start)} - ${formatDate(expectedRange.end)})`, 
        suggestedRange: '' 
      };
    } else {
      return {
        isValid: false,
        message: `⚠️ Date doesn't match ${frequency} cycle ${cycle}`,
        suggestedRange: `Expected range: ${formatDate(expectedRange.start)} - ${formatDate(expectedRange.end)}`
      };
    }
  };

  // Generate cycle options based on inspection frequency
  const generateCycleOptions = (frequency) => {
    if (!frequency) return [];
    
    let maxCycles = 1;
    let cycleName = 'Cycle';
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        maxCycles = 52;
        cycleName = 'Week';
        break;
      case 'monthly':
        maxCycles = 12;
        cycleName = 'Month';
        break;
      case 'quarterly':
        maxCycles = 4;
        cycleName = 'Quarter';
        break;
      case 'biannual':
        maxCycles = 2;
        cycleName = 'Half-Year';
        break;
      case 'annual':
        maxCycles = 1;
        cycleName = 'Year';
        break;
      case 'ad-hoc':
        // For ad-hoc, we'll just show a single option
        return [
          <MenuItem key="1" value="1">
            Ad-hoc Inspection
          </MenuItem>
        ];
      case 'bimonthly':
        maxCycles = 6;
        cycleName = 'Period';
        break;
      case 'every_4_months':
        maxCycles = 3;
        cycleName = 'Period';
        break;
      case 'every_18_months':
      case 'biennial':
        maxCycles = 1;
        cycleName = 'Cycle';
        break;
      default:
        return [
          <MenuItem key="not-applicable" value="1" disabled>
            Cycle not applicable
          </MenuItem>
        ];
    }
    
    const options = [];
    for (let i = 1; i <= maxCycles; i++) {
      options.push(
        <MenuItem key={i} value={i.toString()}>
          {cycleName} {i}
        </MenuItem>
      );
    }
    
    return options;
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.inspection_type_id &&
      formData.inspection_date &&
      formData.cycle &&
      dateValidation.isValid
    );
  };

  return (
    <Dialog
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', mb: 2 }}>
        Add New Inspection for {asset?.name || 'Asset'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Inspection created successfully!
          </Alert>
        )}
        
        {loadingResources ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Asset Information
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Asset: <strong>{enhancedAsset?.name || asset?.name || 'Unknown Asset'}</strong> ({enhancedAsset?.code || asset?.code || 'No Code'})
              </Typography>
              
              {(enhancedAsset?.organization_name || asset?.organization_name) && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Organization: <strong>{enhancedAsset?.organization_name || asset?.organization_name}</strong>
                </Typography>
              )}
              
              {(enhancedAsset?.network_name || asset?.network_name) && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Network: <strong>{enhancedAsset?.network_name || asset?.network_name}</strong>
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Current Rating: 
                {(enhancedAsset?.rating_name || asset?.rating_name) ? (
                  <Chip
                    size="small"
                    label={enhancedAsset?.rating_name || asset?.rating_name}
                    sx={{
                      height: '20px',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      bgcolor: alpha(enhancedAsset?.rating_color_code || asset?.rating_color_code || '#9e9e9e', 0.15),
                      color: enhancedAsset?.rating_color_code || asset?.rating_color_code || '#616161',
                      border: `1px solid ${alpha(enhancedAsset?.rating_color_code || asset?.rating_color_code || '#9e9e9e', 0.3)}`,
                      '& .MuiChip-label': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }
                    }}
                    icon={
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: enhancedAsset?.rating_color_code || asset?.rating_color_code || '#9e9e9e'
                      }} />
                    }
                  />
                ) : (
                  <Chip
                    size="small"
                    label="N/A"
                    sx={{
                      height: '20px',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      bgcolor: alpha('#9e9e9e', 0.1),
                      color: '#616161',
                      border: `1px solid ${alpha('#9e9e9e', 0.2)}`
                    }}
                  />
                )}
              </Typography>
            </Box>
            
            <Divider />
            
            <Typography variant="subtitle1" fontWeight="bold">
              Inspection Details
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel id="inspection-type-label">Inspection Type</InputLabel>
              <Select
                labelId="inspection-type-label"
                id="inspection_type_id"
                name="inspection_type_id"
                value={formData.inspection_type_id}
                onChange={handleChange}
                label="Inspection Type"
                disabled={loading || success}
              >
                {inspectionTypes.length > 0 ? (
                  inspectionTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    No inspection types available for this asset
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            
            {selectedInspectionType && (
              <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="info.contrastText" gutterBottom>
                  Inspection Type Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="info.contrastText">
                      <strong>Frequency:</strong> {selectedInspectionType.cycle_frequency ? 
                        selectedInspectionType.cycle_frequency.charAt(0).toUpperCase() + 
                        selectedInspectionType.cycle_frequency.slice(1).replace('_', ' ') : 
                        'Not specified'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="info.contrastText">
                      <strong>Mandatory:</strong> {selectedInspectionType.is_mandatory ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  {selectedInspectionType.cycle_frequency && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="info.contrastText">
                        <strong>Available Cycles:</strong> {getCycleRangeText(selectedInspectionType.cycle_frequency)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedInspectionType.description && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="info.contrastText">
                        <strong>Description:</strong> {selectedInspectionType.description}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
            
            <FormControl fullWidth>
              <InputLabel id="cycle-label">Inspection Cycle</InputLabel>
              <Select
                labelId="cycle-label"
                id="cycle"
                name="cycle"
                value={formData.cycle}
                onChange={handleChange}
                label="Inspection Cycle"
                disabled={loading || success || !selectedInspectionType}
              >
                {selectedInspectionType ? (
                  generateCycleOptions(selectedInspectionType.cycle_frequency)
                ) : (
                  <MenuItem disabled value="">
                    Select an inspection type first
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            
            {cycleWarning && (
              <Alert severity="warning">
                {cycleWarning}
              </Alert>
            )}

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Inspection Date"
                value={formData.inspection_date}
                onChange={handleDateChange}
                disabled={loading || success}
                format="dd/MM/yyyy"
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    variant: "outlined" 
                  } 
                }}
              />
            </LocalizationProvider>
            
            {dateValidation.message && (
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: dateValidation.isValid ? 'success.light' : 'warning.light',
                border: 1,
                borderColor: dateValidation.isValid ? 'success.main' : 'warning.main'
              }}>
                <Typography 
                  variant="body2" 
                  color={dateValidation.isValid ? 'success.contrastText' : 'warning.contrastText'}
                  sx={{ fontWeight: 'medium' }}
                >
                  {dateValidation.message}
                </Typography>
                {dateValidation.suggestedRange && (
                  <Typography 
                    variant="caption" 
                    color={dateValidation.isValid ? 'success.contrastText' : 'warning.contrastText'}
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {dateValidation.suggestedRange}
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              id="notes"
              name="notes"
              label="Notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              disabled={loading || success}
              variant="outlined"
            />
          </Stack>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button 
          onClick={onClose} 
          color="inherit"
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary"
          variant="contained"
          disabled={!isFormValid() || loading || success}
          sx={{ px: 3 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Inspection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInspectionModal; 
